import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  TablePagination, 
  Chip, 
  TextField, 
  Button,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  MenuItem,
  Autocomplete,
  Stack,
  Menu,
  ListItemIcon,
  ListItemText,
  IconButton,
  Dialog,
  Alert,
  AlertTitle
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningIcon from '@mui/icons-material/Warning';
import CloseIcon from '@mui/icons-material/Close';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB } from 'date-fns/locale';
import { formatDateForDisplay } from '../../utils/dateUtils';
import { AppointmentStatus, Appointment as AppointmentType } from '../../features/appointments/types/appointment.types';
import { AppointmentOrderData } from '../../features/appointments/types/appointmentOrders.types';
import { appointmentApi } from '../../features/appointments/api/appointmentApi';
import { appointmentOrderApi } from '../../features/appointment-orders/api/appointmentOrdersApi';
import { hospitalApi } from '../../api/hospitalApi';
import AppointmentDetails from '../../features/appointments/components/AppointmentDetails';

// Extended appointment type with dispensing info
type AppointmentWithDispensing = AppointmentType & {
  pendingDispensingCount?: number;
  totalPendingQuantity?: number;
  orders?: AppointmentOrderData[];
};

const PendingDispensingReport: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [startDate, setStartDate] = useState<Date | null>(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });
  const [endDate, setEndDate] = useState<Date | null>(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return today;
  });
  const [statusFilter, setStatusFilter] = useState<AppointmentStatus | 'all'>('all');
  const [doctorFilter, setDoctorFilter] = useState<string>('all');
  const [selectedPatient, setSelectedPatient] = useState<{ id: string; name: string } | null>(null);
  
  // Appointment data state
  const [appointments, setAppointments] = useState<AppointmentWithDispensing[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  
  // Summary totals
  const [summaryTotals, setSummaryTotals] = useState({
    totalOrders: 0,
    totalPendingQuantity: 0,
    appointmentCount: 0
  });
  
  // Ref for PDF generation
  const reportRef = React.useRef<HTMLDivElement>(null);
  
  // Hospital info state
  const [hospitalInfo, setHospitalInfo] = useState<any>(null);
  
  // PDF menu state
  const [pdfMenuAnchor, setPdfMenuAnchor] = useState<null | HTMLElement>(null);
  
  // Appointment dialog state
  const [selectedAppointment, setSelectedAppointment] = useState<AppointmentWithDispensing | null>(null);
  const [isAppointmentDialogOpen, setIsAppointmentDialogOpen] = useState(false);
  const [isLoadingAppointment, setIsLoadingAppointment] = useState(false);
  
  // Alert state for business logic warnings
  const [businessAlerts, setBusinessAlerts] = useState<Array<{
    id: string;
    type: 'warning' | 'error' | 'info';
    title: string;
    message: string;
    orderDetails?: any;
  }>>([]);
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  
  // Doctors list
  const [doctors, setDoctors] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => {
    import('../../features/employees/api/employeeApi').then(({ employeeApi }) => {
      employeeApi.getDoctors().then((data) => {
        setDoctors(data.map(doc => ({ id: doc.id, name: `${doc.firstName} ${doc.lastName}`.trim() }))); 
      });
    });
  }, []);

  // Fetch hospital info on component mount
  useEffect(() => {
    const fetchHospitalInfo = async () => {
      try {
        const hospital = await hospitalApi.getCompanyProfile();
        setHospitalInfo(hospital);
      } catch (error) {
        console.error('Error fetching hospital info:', error);
      }
    };
    fetchHospitalInfo();
  }, []);

  // Fetch appointments with pending dispensing
  const fetchAppointments = async () => {
    if (!startDate || !endDate) return;
    
    setLoading(true);
    try {
      // Get all appointment orders
      const orderParams = {
        page: 1,
        limit: 1000,
      } as any;

      const orders = await appointmentOrderApi.getAll(orderParams);
      
      // Check for business logic inconsistencies
      const alerts: Array<{
        id: string;
        type: 'warning' | 'error' | 'info';
        title: string;
        message: string;
        orderDetails?: any;
      }> = [];
      
      orders.forEach(order => {
        // Check for orders with pending dispensing
        const pendingQuantity = (order.quantity || 0) - ((order as any).dispensedQuantity || 0);
        
        if (pendingQuantity > 0 && order.financialStatus === 'PAID') {
          alerts.push({
            id: `pending-dispensing-${order.id}`,
            type: 'warning',
            title: 'Paid Item Pending Dispensing',
            message: `Order ${order.orderNumber} for "${order.item?.itemName}" (${formatDateForDisplay(new Date(order.appointment?.appointmentDate || order.orderDate))}) is paid but has ${pendingQuantity} units pending dispensing.`,
            orderDetails: {
              orderId: order.id,
              orderNumber: order.orderNumber,
              itemName: order.item?.itemName,
              totalQuantity: order.quantity,
              dispensedQuantity: (order as any).dispensedQuantity || 0,
              pendingQuantity: pendingQuantity,
              appointmentId: order.appointment?.id
            }
          });
        }
        
        // Check for over-dispensing
        if (((order as any).dispensedQuantity || 0) > (order.quantity || 0)) {
          alerts.push({
            id: `over-dispensed-${order.id}`,
            type: 'error',
            title: 'Over-Dispensed Item',
            message: `Order ${order.orderNumber} (${formatDateForDisplay(new Date(order.appointment?.appointmentDate || order.orderDate))}) has been over-dispensed. Dispensed: ${(order as any).dispensedQuantity || 0}, Ordered: ${order.quantity}`,
            orderDetails: {
              orderId: order.id,
              orderNumber: order.orderNumber,
              itemName: order.item?.itemName,
              totalQuantity: order.quantity,
              dispensedQuantity: (order as any).dispensedQuantity || 0
            }
          });
        }
      });
      
      setBusinessAlerts(alerts);
      
      // Filter orders with pending dispensing and date range
      const pendingDispensingOrders = orders.filter(order => {
        // Check pending quantity
        const pendingQuantity = (order.quantity || 0) - ((order as any).dispensedQuantity || 0);
        const hasPendingDispensing = pendingQuantity > 0;
        
        // Check date range (using appointment date from order)
        const orderDate = new Date(order.appointment?.appointmentDate || order.createdAt);
        const isInDateRange = orderDate >= startDate && orderDate <= endDate;
        
        return hasPendingDispensing && isInDateRange;
      });

      // Group orders by appointment
      const appointmentOrdersMap = new Map();
      pendingDispensingOrders.forEach(order => {
        const appointmentId = order.appointment?.id;
        if (appointmentId) {
          if (!appointmentOrdersMap.has(appointmentId)) {
            appointmentOrdersMap.set(appointmentId, {
              appointment: order.appointment,
              orders: [],
              totalPendingQuantity: 0
            });
          }
          const appointmentData = appointmentOrdersMap.get(appointmentId);
          appointmentData.orders.push(order);
          
          const pendingQuantity = (order.quantity || 0) - ((order as any).dispensedQuantity || 0);
          appointmentData.totalPendingQuantity += pendingQuantity;
        }
      });

      // Convert to appointments array with pending dispensing info
      const appointmentsWithPending = Array.from(appointmentOrdersMap.values())
        .map(({ appointment, orders, totalPendingQuantity }) => ({
          ...appointment,
          pendingDispensingCount: orders.length,
          totalPendingQuantity,
          orders
        }))
        .filter(appointment => {
          // Apply filters
          if (statusFilter !== 'all' && appointment.status !== statusFilter) return false;
          if (doctorFilter !== 'all' && appointment.doctorId !== doctorFilter) return false;
          if (selectedPatient && appointment.patientId !== selectedPatient.id) return false;
          return true;
        });

      // Apply pagination
      const startIndex = page * rowsPerPage;
      const endIndex = startIndex + rowsPerPage;
      const paginatedAppointments = appointmentsWithPending.slice(startIndex, endIndex);
      
      setAppointments(paginatedAppointments);
      setTotalCount(appointmentsWithPending.length);
      
      // Calculate summary totals
      const totals = appointmentsWithPending.reduce((acc, appointment) => ({
        totalOrders: acc.totalOrders + (appointment.pendingDispensingCount || 0),
        totalPendingQuantity: acc.totalPendingQuantity + (appointment.totalPendingQuantity || 0),
        appointmentCount: acc.appointmentCount + 1
      }), { totalOrders: 0, totalPendingQuantity: 0, appointmentCount: 0 });
      
      setSummaryTotals(totals);
      
    } catch (error) {
      console.error('Error fetching appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data when filters change
  useEffect(() => {
    fetchAppointments();
  }, [page, rowsPerPage, startDate, endDate, statusFilter, doctorFilter, selectedPatient]);

  // Handle page change
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle status filter change
  const handleStatusFilterChange = (event: SelectChangeEvent) => {
    setStatusFilter(event.target.value as AppointmentStatus | 'all');
    setPage(0);
  };

  // Handle doctor filter change
  const handleDoctorFilterChange = (event: SelectChangeEvent) => {
    setDoctorFilter(event.target.value);
    setPage(0);
  };

  // Handle view appointment details
  const handleViewAppointment = async (appointment: AppointmentWithDispensing) => {
    if (!appointment || !appointment.id) {
      console.error('No valid appointment provided');
      return;
    }
    
    setIsLoadingAppointment(true);
    setIsAppointmentDialogOpen(true);
    
    try {
      const fullAppointment = await appointmentApi.getById(appointment.id);
      setSelectedAppointment(fullAppointment);
    } catch (error) {
      console.error('Error fetching appointment details:', error);
      setSelectedAppointment(appointment);
    } finally {
      setIsLoadingAppointment(false);
    }
  };

  // Handle close appointment dialog
  const handleCloseAppointmentDialog = () => {
    setIsAppointmentDialogOpen(false);
    setSelectedAppointment(null);
    fetchAppointments();
  };

  // PDF Export Functions
  const handlePdfMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setPdfMenuAnchor(event.currentTarget);
  };

  const handlePdfMenuClose = () => {
    setPdfMenuAnchor(null);
  };

  const generatePDF = async (includeDetails: boolean = false) => {
    if (!reportRef.current) return;

    try {
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 30;

      pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
      
      const fileName = includeDetails 
        ? `pending-dispensing-report-detailed-${new Date().toISOString().split('T')[0]}.pdf`
        : `pending-dispensing-report-summary-${new Date().toISOString().split('T')[0]}.pdf`;
      
      pdf.save(fileName);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
    
    handlePdfMenuClose();
  };

  // Get status color
  const getStatusColor = (status: AppointmentStatus): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case 'SCHEDULED': return 'info';
      case 'CONFIRMED': return 'primary';
      case 'ARRIVED': return 'warning';
      case 'IN_PROGRESS': return 'secondary';
      case 'COMPLETED': return 'success';
      case 'CANCELLED': return 'error';
      case 'NO_SHOW': return 'error';
      case 'RESCHEDULED': return 'warning';
      default: return 'default';
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Pending Dispensing Report
        </Typography>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(newValue) => {
                    setStartDate(newValue);
                    setPage(0);
                  }}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(newValue) => {
                    setEndDate(newValue);
                    setPage(0);
                  }}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={handleStatusFilterChange}
                  >
                    <MenuItem value="all">All Statuses</MenuItem>
                    <MenuItem value="SCHEDULED">Scheduled</MenuItem>
                    <MenuItem value="CONFIRMED">Confirmed</MenuItem>
                    <MenuItem value="ARRIVED">Arrived</MenuItem>
                    <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
                    <MenuItem value="COMPLETED">Completed</MenuItem>
                    <MenuItem value="CANCELLED">Cancelled</MenuItem>
                    <MenuItem value="NO_SHOW">No Show</MenuItem>
                    <MenuItem value="RESCHEDULED">Rescheduled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Doctor</InputLabel>
                  <Select
                    value={doctorFilter}
                    label="Doctor"
                    onChange={handleDoctorFilterChange}
                  >
                    <MenuItem value="all">All Doctors</MenuItem>
                    {doctors.map((doctor) => (
                      <MenuItem key={doctor.id} value={doctor.id}>
                        {doctor.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={fetchAppointments}
                    size="small"
                  >
                    Refresh
                  </Button>
                  {businessAlerts.length > 0 && (
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={<WarningIcon />}
                      onClick={() => setAlertDialogOpen(true)}
                      size="small"
                    >
                      Alerts ({businessAlerts.length})
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    endIcon={<ArrowDropDownIcon />}
                    onClick={handlePdfMenuClick}
                    size="small"
                  >
                    Export
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4} md={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Appointments with Pending Items
                </Typography>
                <Typography variant="h4">
                  {summaryTotals.appointmentCount}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4} md={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Total Pending Orders
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {summaryTotals.totalOrders}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4} md={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Total Pending Quantity
                </Typography>
                <Typography variant="h4" color="error.main">
                  {summaryTotals.totalPendingQuantity}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Alerts Dialog */}
        <Dialog
          open={alertDialogOpen}
          onClose={() => setAlertDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6">
              Business Logic Alerts ({businessAlerts.length})
            </Typography>
            <IconButton onClick={() => setAlertDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ p: 2, maxHeight: '60vh', overflowY: 'auto' }}>
            {businessAlerts.map((alert) => (
              <Alert 
                key={alert.id}
                severity={alert.type} 
                sx={{ mb: 2 }}
                action={
                  <IconButton
                    aria-label="close"
                    color="inherit"
                    size="small"
                    onClick={() => {
                      setBusinessAlerts(prev => prev.filter(a => a.id !== alert.id));
                      if (businessAlerts.length === 1) {
                        setAlertDialogOpen(false);
                      }
                    }}
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                }
              >
                <AlertTitle>{alert.title}</AlertTitle>
                {alert.message}
                {alert.orderDetails && (
                  <Box sx={{ mt: 1, fontSize: '0.875rem', opacity: 0.8 }}>
                    Order ID: {alert.orderDetails.orderId} | 
                    Pending: {alert.orderDetails.pendingQuantity} units |
                    Dispensed: {alert.orderDetails.dispensedQuantity}/{alert.orderDetails.totalQuantity}
                  </Box>
                )}
              </Alert>
            ))}
          </Box>
          <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between' }}>
            <Button
              onClick={() => {
                setBusinessAlerts([]);
                setAlertDialogOpen(false);
              }}
              startIcon={<CloseIcon />}
              color="error"
            >
              Dismiss All
            </Button>
            <Button onClick={() => setAlertDialogOpen(false)} variant="contained">
              Close
            </Button>
          </Box>
        </Dialog>

        {/* Report Content */}
        <div ref={reportRef}>
          {/* Hospital Header for PDF */}
          <Box sx={{ display: 'none', '@media print': { display: 'block' } }}>
            {hospitalInfo && (
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography variant="h4" gutterBottom>
                  {hospitalInfo.name}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {hospitalInfo.address}
                </Typography>
                <Typography variant="h5" sx={{ mt: 2 }}>
                  Pending Dispensing Report
                </Typography>
                <Typography variant="body2">
                  Period: {startDate ? formatDateForDisplay(startDate) : ''} to {endDate ? formatDateForDisplay(endDate) : ''}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Appointments Table */}
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Patient</TableCell>
                    <TableCell>Doctor</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Pending Items</TableCell>
                    <TableCell align="center">Pending Quantity</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography>Loading...</Typography>
                      </TableCell>
                    </TableRow>
                  ) : appointments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography>No appointments with pending dispensing found.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    appointments.map((appointment) => (
                      <TableRow key={appointment.id} hover>
                        <TableCell>
                          {formatDateForDisplay(new Date(appointment.appointmentDate))}
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {appointment.patient?.fullName}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              MRN: {appointment.patient?.mrn}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {appointment.doctor?.firstName} {appointment.doctor?.lastName}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={appointment.status}
                            color={getStatusColor(appointment.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={appointment.pendingDispensingCount || 0}
                            color="warning"
                            size="small"
                            icon={<LocalPharmacyIcon />}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography color="error.main" fontWeight="medium">
                            {appointment.totalPendingQuantity || 0}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => handleViewAppointment(appointment)}
                            title="View Details & Dispense"
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </div>

        {/* PDF Export Menu */}
        <Menu
          anchorEl={pdfMenuAnchor}
          open={Boolean(pdfMenuAnchor)}
          onClose={handlePdfMenuClose}
        >
          <MenuItem onClick={() => generatePDF(false)}>
            <ListItemIcon>
              <PictureAsPdfIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Export Summary PDF</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => generatePDF(true)}>
            <ListItemIcon>
              <PictureAsPdfIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Export Detailed PDF</ListItemText>
          </MenuItem>
        </Menu>

        {/* Appointment Details Dialog */}
        {isAppointmentDialogOpen && (
          <Dialog
            open={isAppointmentDialogOpen}
            onClose={handleCloseAppointmentDialog}
            maxWidth="lg"
            fullWidth
          >
            {isLoadingAppointment ? (
              <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Typography>Loading appointment details...</Typography>
              </Box>
            ) : selectedAppointment ? (
              <AppointmentDetails
                appointment={selectedAppointment}
                onClose={handleCloseAppointmentDialog}
              />
            ) : (
              <Box sx={{ p: 4 }}>
                <Typography color="error">Failed to load appointment details</Typography>
              </Box>
            )}
          </Dialog>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default PendingDispensingReport;
